import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import lodashCompact from 'lodash/fp/compact';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import { DATA_TABLE_SOURCE_SUNSETUP_LISTING_ORDERS } from 'common/dataTableConstants';

import { JOB_TYPES } from 'common/jobConstants';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  SR_API_ACTION_DRIVERS_GET_LIST,
  SR_API_ACTION_JOB_ORDER_CREATE,
  SR_API_ACTION_JOB_ORDER_EDIT,
} from 'api/actionTypes';

import {
  SCREEN_ID_SUNSETUP_CREATE_ORDER,
  SCREEN_ID_SUNSETUP_UPDATE_ORDER,
} from 'client/constants/screenIds';

import { actionDataTableReset } from 'client/helpers/dataTables';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import DriverActions from 'client/actions/Driver';
import JobOrderActions from 'client/actions/JobOrder';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrUpsertJobOrderForm from 'client/components/SrUpsertJobOrderForm';
import SrCard, {
  SR_CARD_BULLET_COLOR_PURPLE,
  SR_CARD_CONTENT_TYPE_FORM_REGULAR,
} from 'client/components/SrCard/SrCard';

@withTransitionHook
@withScreenId(({ match }) =>
  match.params.jobOrderId ? SCREEN_ID_SUNSETUP_UPDATE_ORDER : SCREEN_ID_SUNSETUP_CREATE_ORDER,
)
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => {
  const cursors = {
    driversByMarketId: ['drivers', 'byMarketId'],
  };
  if (match.params.jobOrderId) {
    cursors.jobOrder = ['jobOrders', 'byId', match.params.jobOrderId];
  }
  return cursors;
})
@withScrollToTop
class SunSetupUpsertJobOrderPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Create Job Order | Sunroom Rentals',
  });

  static propTypes = {
    driversByMarketId: PropTypes.object.isRequired,
    jobOrder: PropTypes.object,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    const jobOrderId = parseInt(match.params.jobOrderId, 10) || null;

    return Promise.all(
      lodashCompact([
        DriverActions.getDriverList(inject),
        jobOrderId && JobOrderActions.getById(inject, { jobOrderId, reload: true }),
      ]),
    );
  }

  _getBackUrl = () => {
    const { location } = this.props;
    const backUrl = queryString.parse(location.search).backUrl || null;
    return backUrl;
  };

  _getListingId = () => {
    const { location, jobOrder } = this.props;
    const urlMatchListingId = parseInt(queryString.parse(location.search).listingId, 10) || null;
    return (jobOrder && jobOrder.listingId) || urlMatchListingId || null;
  };

  _onSubmit = (values) => {
    const { dispatch, history, match } = this.props;

    const jobOrderId = parseInt(match.params.jobOrderId, 10) || null;
    const backUrl = this._getBackUrl();

    const jobs = JOB_TYPES.filter((jobTypeId) => values[jobTypeId]).map((jobTypeId) => ({
      jobTypeId,
    }));

    if (jobs.length === 0) {
      global.alert('Please select at least 1 job.');
      return;
    }

    let payload = setNullEmptyValues({
      listingId: values.listingId,
      dueDate: values.dueDate,
      driverId: values.driverId,
      jobs: jobs,
      instructions: values.instructions,
      status: values.status,
    });

    if (jobOrderId) {
      payload = {
        jobOrderId,
        updates: payload,
      };
    }

    dispatch(postWithState, {
      action: jobOrderId ? SR_API_ACTION_JOB_ORDER_EDIT : SR_API_ACTION_JOB_ORDER_CREATE,
      payload,
      onSuccess() {
        global.alert(`Job order ${jobOrderId ? 'updated' : 'created'}!`);
        Promise.resolve()
          .then(() => {
            if (jobOrderId) {
              return dispatch(JobOrderActions.getById, { jobOrderId, reload: true });
            }
          })
          .then(() => {
            return dispatch(actionDataTableReset, {
              dataSource: DATA_TABLE_SOURCE_SUNSETUP_LISTING_ORDERS,
            });
          })
          .then(() => {
            if (backUrl) {
              history.replace(backUrl);
            }
          });
      },
      onError(error) {
        log.error(error);
        global.alert('Error: ' + (error.originalMessage || error.message));
      },
    });
  };

  _onListingChange = (listing) => {
    const { dispatch } = this.props;
    if (listing.marketId) {
      dispatch(postWithState, {
        action: SR_API_ACTION_DRIVERS_GET_LIST,
        payload: {
          marketId: listing.marketId,
        },
      });
    }
  };

  render() {
    const { driversByMarketId, jobOrder, isTransitioning, match } = this.props;

    if (isTransitioning) {
      return <div className="loading-gif" />;
    }

    const backUrl = this._getBackUrl();
    const isEdit = !!match.params.jobOrderId;
    const listingId = this._getListingId();

    const pageTitle = isEdit ? `Edit Job Order #${match.params.jobOrderId}` : 'Create Job Order';

    const formTitle = isEdit ? 'Edit Order Form' : 'Order Form';

    return (
      <SrPartnerPageV2 navbarTitle={pageTitle} backUrl={backUrl}>
        <SrCard
          color={SR_CARD_BULLET_COLOR_PURPLE}
          contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
          header={formTitle}
        >
          <SrUpsertJobOrderForm
            onSubmit={this._onSubmit}
            onListingChange={this._onListingChange}
            driversByMarketId={driversByMarketId}
            jobOrder={jobOrder}
            listingId={listingId}
          />
        </SrCard>
      </SrPartnerPageV2>
    );
  }
}

SunSetupUpsertJobOrderPage.displayName = 'SunSetupUpsertJobOrderPage';

export default SunSetupUpsertJobOrderPage;
